import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateSkillCollectionListQueries } from "./cache-utils";
import type { SkillCollectionRecord } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

interface UpdateSkillCollectionParams {
  workspaceId: number;
  collectionId: string;
  name?: string;
  description?: string | null;
}

interface UpdateSkillCollectionResponse {
  success: boolean;
  skill_collection: SkillCollectionRecord;
}

export const useUpdateSkillCollection = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      collectionId,
      ...body
    }: UpdateSkillCollectionParams): Promise<UpdateSkillCollectionResponse> => {
      const res = await fetch(ENDPOINTS.skill_collections.get(workspaceId, collectionId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      });
      return handleResponse(res);
    },
    onSuccess: async (_, variables) => {
      invalidateSkillCollectionListQueries(queryClient, variables.workspaceId);
      queryClient.invalidateQueries({
        queryKey: ["skill-collection", variables.workspaceId, variables.collectionId],
      });
    },
    onError: (error: Error) => {
      displayErrorToast(error.message);
    },
  });
};
